import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { isMSP } from "../../helpers/util_helper";
import { getInvoiceDownload, getInvoicePreview, generateInvoice, getCustomerPurchaseOrders } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { getCustomers, getMspBillingMetrics } from "../../store/billingManagement/billingManagementSlice";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEAR_OPTIONS = Array.from({ length: Math.max(0, new Date().getFullYear() + 1 - 2025) }, (_, i) => 2025 + i).reverse();

const BillingManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Check if user is MSP
  const isMSPUser = isMSP();

  // Redux state
  const {
    customers,
    isFetchingCustomers,
    fetchCustomersError,
    pagination,
    mspMetrics,
    isFetchingMetrics,
    metricsError,
  } = useSelector((state) => state.billingManagement);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [previewingInvoiceId, setPreviewingInvoiceId] = useState(null);
  const [downloadSnackbar, setDownloadSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Generate invoice dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateCustomer, setGenerateCustomer] = useState(null);
  const [generateYear, setGenerateYear] = useState(() => Math.max(2025, new Date().getFullYear()));
  const [generateMonth, setGenerateMonth] = useState(() => new Date().getMonth() + 1);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateNoRules, setGenerateNoRules] = useState(false);
  const [generateNoRulesMessage, setGenerateNoRulesMessage] = useState("");
  const [generateError, setGenerateError] = useState(null);
  const [generatePoList, setGeneratePoList] = useState([]);
  const [generatePoId, setGeneratePoId] = useState("");

  // Redirect if not MSP user
  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  // Fetch customers and MSP metrics on mount / refresh
  useEffect(() => {
    if (isMSPUser) {
      dispatch(getCustomers({ page, page_size: pageSize }));
      dispatch(getMspBillingMetrics());
    }
  }, [dispatch, isMSPUser, page, pageSize]);

  // Format currency: full number below 1M, compact (1M, 1.5B) only for 1M and above (same as ConfigureBillingProfile/CustomerInvoices)
  const formatCurrency = (value) => {
    if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
    const num = typeof value === "string" ? parseFloat(value, 10) : value;
    if (Number.isNaN(num)) return "—";
    if (Math.abs(num) >= 1_000_000) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  // Download invoice: get pre-signed URL and trigger file download
  const handleDownloadInvoice = async (customerOrgCode, invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setDownloadingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoiceDownload(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        // Pre-signed S3 URL: open in new tab so the browser triggers the file download
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Download started.", severity: "success" });
      } else {
        // API error: get() returns { status, data } on 4xx, so message is in response.data
        const data = response?.data ?? response;
        const msg = data != null ? getApiErrorMessage(data) : (response?.message || "Download not available for this invoice.");
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data != null ? getApiErrorMessage(data) : (error?.message || "Download failed.");
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleOpenGenerateDialog = (customer) => {
    setGenerateCustomer(customer);
    setGenerateYear(Math.max(2025, new Date().getFullYear()));
    setGenerateMonth(new Date().getMonth() + 1);
    setGenerateNoRules(false);
    setGenerateNoRulesMessage("");
    setGenerateError(null);
    setGeneratePoId("");
    setGenerateDialogOpen(true);
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setGenerateCustomer(null);
    setGenerateNoRules(false);
    setGenerateError(null);
    setGenerateLoading(false);
    setGeneratePoList([]);
    setGeneratePoId("");
  };

  useEffect(() => {
    if (!generateDialogOpen || !generateCustomer?.org_code) return;
    getCustomerPurchaseOrders(generateCustomer.org_code)
      .then((res) => {
        const data = res?.data ?? res;
        setGeneratePoList(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => setGeneratePoList([]));
  }, [generateDialogOpen, generateCustomer?.org_code]);

  const handleGenerateInvoice = async (overrideWarnings = false) => {
    if (!generateCustomer?.org_code) return;
    const year = Number(generateYear);
    const month = Number(generateMonth);
    if (month < 1 || month > 12) {
      setGenerateError("Month must be between 1 and 12.");
      return;
    }
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      const payload = { year, month, override_warnings: overrideWarnings };
      if (generatePoId && generatePoId.trim()) payload.po_id = generatePoId.trim();
      const response = await generateInvoice(generateCustomer.org_code, payload);
      const data = response?.data ?? response;
      const message = data?.message ?? response?.message ?? "";
      const status = response?.status;
      if (status === 200 || (data && (status == null || status === 200))) {
        const msgLower = String(message).toLowerCase();
        if (!overrideWarnings && (msgLower.includes("no rules") || msgLower.includes("no rules found"))) {
          setGenerateNoRules(true);
          setGenerateNoRulesMessage(message || "No rules found for the selected date range.");
        } else {
          setDownloadSnackbar({ open: true, message: message || "Invoice generation request accepted.", severity: "success" });
          handleCloseGenerateDialog();
          dispatch(getCustomers({ page, page_size: pageSize }));
        }
      } else {
        setGenerateError(getApiErrorMessage(data));
      }
    } catch (error) {
      const errData = error?.response?.data ?? error?.data;
      setGenerateError(getApiErrorMessage(errData) || error?.message || "Request failed.");
    } finally {
      setGenerateLoading(false);
    }
  };

  // Download allowed only when invoice status is Finalized or Sent
  const canDownloadInvoice = (invoice) => {
    if (!invoice?.invoice_id) return false;
    const status = (invoice?.ui_status || invoice?.status || "").toString().toUpperCase();
    return status === "FINALIZED" || status === "SENT";
  };

  // Preview invoice: get pre-signed URL and open in new tab (same as CustomerInvoices)
  const handlePreviewInvoice = async (customerOrgCode, invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setPreviewingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoicePreview(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Preview opened in new tab.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data != null ? getApiErrorMessage(data) : (response?.message || "Preview not available for this invoice.");
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data != null ? getApiErrorMessage(data) : (error?.message || "Preview failed.");
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setPreviewingInvoiceId(null);
    }
  };

  // Short invoice ref for table display (e.g. INV-1069c from invoice_id)
  const getShortInvoiceRef = (invoiceId) => {
    if (!invoiceId) return null;
    const clean = String(invoiceId).replace(/-/g, "");
    return clean.length >= 5 ? `INV-${clean.slice(-5)}` : `INV-${clean}`;
  };

  // Format date (parse YYYY-MM-DD manually to avoid timezone off-by-one)
  const formatDate = (dateString) => {
    if (!dateString) return "--";
    try {
      const parts = String(dateString).split("-");
      if (parts.length >= 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
          const date = new Date(year, month, day);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        }
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "--";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "--";
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    // Remove common words and split
    const cleanName = name.replace(/\b(Co|Inc|LLC|Ltd|Corp|SubOrg)\b/gi, "").trim();
    const words = cleanName.split(/\s+/).filter(w => w.length > 0);
    
    if (words.length >= 2) {
      // Take first letter of first two words
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1 && words[0].length >= 2) {
      // If only one word, take first two letters
      return words[0].substring(0, 2).toUpperCase();
    } else if (words.length === 1) {
      // Single character word
      return words[0][0].toUpperCase() + words[0][0].toUpperCase();
    }
    return "?";
  };

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    // Defensive check: ensure customers is an array
    if (!customers) {
      console.log("BillingManagement: customers is undefined/null");
      return [];
    }
    if (!Array.isArray(customers)) {
      console.log("BillingManagement: customers is not an array:", typeof customers, customers);
      return [];
    }
    if (!searchTerm) return customers;

    const searchLower = searchTerm.toLowerCase();
    return customers.filter((customer) => {
      if (!customer) return false;
      return (
        customer.org_name?.toLowerCase().includes(searchLower) ||
        customer.org_code?.toLowerCase().includes(searchLower) ||
        customer.accounts?.some((acc) =>
          acc?.toLowerCase().includes(searchLower)
        )
      );
    });
  }, [customers, searchTerm]);

  // Short month label for card titles e.g. "Jan" (function to avoid HMR/scope issues)
  const getMetricsMonthBracket = () => {
    const start = mspMetrics?.start_date;
    if (!start) return null;
    const parts = String(start).split("-").map(Number);
    const y = parts[0];
    const m = parts[1];
    if (!y || !m) return null;
    const date = new Date(y, m - 1, 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  // Single consistent avatar color for all
  const getAvatarColor = () => {
    return { bg: "#3b82f6", text: "#ffffff" }; // Consistent blue
  };

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Header row: title left, Configure billing profile button right */}
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#0f172a",
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
              lineHeight: 1.2,
            }}
          >
            Billing Overview
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.8125rem",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Manage your customer billing, generate invoices, and track revenue.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => history.push("/billing-management/configure-profile")}
          sx={{
            backgroundColor: "#3b82f6",
            color: "#ffffff",
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.625,
            height: "32px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#2563eb",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          Configure Billing Profiles
        </Button>
      </Box>

      {/* MSP Billing Metrics — dashboard-style stat cards */}
      <Box sx={{ mb: 3 }}>
        {metricsError && (
          <Box sx={{ p: 2, mb: 2, borderRadius: "8px", bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#b91c1c" }}>
              {typeof metricsError === "string" ? metricsError : "Failed to load metrics."}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          {/* Current month — dashboard card with accent */}
          <Box
            sx={{
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)",
            }}
          >
            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "#3b82f6" }} />
            <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>
                {getMetricsMonthBracket() || "Current month"}
              </Typography>
              {isFetchingMetrics ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
                </Box>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Total Billed</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.total_billed != null ? formatCurrency(mspMetrics.total_billed) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Net Usage Charges</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.net_usage_last_month != null ? formatCurrency(mspMetrics.net_usage_last_month) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Fixed Charges</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.fixed_charges_last_month != null ? formatCurrency(mspMetrics.fixed_charges_last_month) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Billed Customers</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.billed_customers != null ? mspMetrics.billed_customers : "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* YTD — dashboard card with accent */}
          <Box
            sx={{
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)",
            }}
          >
            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "#64748b" }} />
            <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>
                Year to date
              </Typography>
              {isFetchingMetrics ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
                </Box>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Total Billed</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.ytd_total_billed != null ? formatCurrency(mspMetrics.ytd_total_billed) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Net Usage Charges</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.net_usage_ytd != null ? formatCurrency(mspMetrics.net_usage_ytd) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>Fixed Charges</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {mspMetrics?.fixed_charges_ytd != null ? formatCurrency(mspMetrics.fixed_charges_ytd) : "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Customer List Table — shadcn-style */}
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "#fff", fontFamily: '"Inter", system-ui, sans-serif' }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "rgba(0,0,0,0.06)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search customers or invoices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#71717a" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: 380,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  backgroundColor: "#fafafa",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "6px",
                  height: "36px",
                  "&:hover": { borderColor: "rgba(0,0,0,0.12)", backgroundColor: "#fff" },
                  "&.Mui-focused": { borderColor: "#18181b", backgroundColor: "#fff", boxShadow: "0 0 0 2px rgba(24,24,27,0.1)" },
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Box>

          {/* Loading State */}
          {isFetchingCustomers && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Loading customers...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {fetchCustomersError && !isFetchingCustomers && (
            <Box
              sx={{
                p: 3,
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #f87171",
                m: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#991b1b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {typeof fetchCustomersError === "string"
                  ? fetchCustomersError
                  : "An error occurred while fetching customers."}
              </Typography>
            </Box>
          )}

          {/* Table — shadcn-style: even padding, border-b only, hover:bg-muted/50 */}
          {!isFetchingCustomers && !fetchCustomersError && (
            <TableContainer sx={{ maxHeight: "calc(100vh - 400px)" }}>
              <Table stickyHeader size="medium" sx={{ minWidth: 720, fontFamily: "inherit", fontSize: "0.875rem", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow sx={{ "& th": { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" } }}>
                    <TableCell sx={{ fontFamily: "inherit", width: "28%" }}>Customer</TableCell>
                    <TableCell sx={{ fontFamily: "inherit", width: "14%" }}>Last bill date</TableCell>
                    <TableCell sx={{ fontFamily: "inherit", width: "14%" }}>Last Billed Amount</TableCell>
                    <TableCell sx={{ fontFamily: "inherit", width: "14%" }}>Next bill date</TableCell>
                    <TableCell sx={{ fontFamily: "inherit", width: "14%" }}>Billing rules</TableCell>
                    <TableCell align="center" sx={{ fontFamily: "inherit", width: "16%", px: 1.5 }}>Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, px: 3, fontSize: "0.875rem", color: "#71717a", fontFamily: "inherit" }}>
                        No customers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((customer, index) => {
                      const billingRulesCount = customer.billing_rules_count ?? customer.rule_metrics?.total ?? 0;
                      return (
                        <TableRow
                          key={customer.org_code || index}
                          sx={{
                            "& td": { borderBottom: "1px solid #e4e4e7", py: 1.5, px: 3, fontSize: "0.875rem", fontFamily: "inherit" },
                            "&:hover": { bgcolor: "#fafafa" },
                          }}
                        >
                          <TableCell sx={{ fontFamily: "inherit" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: getAvatarColor().bg, color: "#fff", fontSize: "0.8125rem", fontWeight: 500 }}>
                                {getInitials(customer.org_name)}
                              </Avatar>
                              <Typography component="span" sx={{ fontWeight: 500, color: "#18181b", fontSize: "0.875rem", fontFamily: "inherit" }}>
                                {customer.org_name || "—"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: "#71717a", fontFamily: "inherit" }}>
                            {customer.last_bill_date ? formatDate(customer.last_bill_date) : "—"}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "inherit" }}>
                            {customer.latest_invoice?.total_calculated_amount != null ? (
                              <Box>
                                <Typography component="span" sx={{ fontWeight: 600, color: "#18181b", fontSize: "0.875rem", fontFamily: "inherit" }}>
                                  {formatCurrency(customer.latest_invoice.total_calculated_amount)}
                                </Typography>
                                {(() => {
                                  const inv = customer.latest_invoice;
                                  const invDisplay = (inv?.invoice_display != null && String(inv.invoice_display).trim() !== "")
                                    ? (() => { const d = String(inv.invoice_display).trim(); return d.startsWith("INV-") ? d : `INV-${d}`; })()
                                    : getShortInvoiceRef(inv?.invoice_id);
                                  const canPreview = canDownloadInvoice(inv);
                                  const isPreviewing = previewingInvoiceId === inv?.invoice_id;
                                  return invDisplay ? (
                                    <Box component="span" sx={{ display: "block", fontSize: "0.75rem", fontFamily: "inherit", mt: 0.25 }}>
                                      {isPreviewing ? (
                                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                          <CircularProgress size={12} sx={{ color: "#3b82f6" }} />
                                          <span>{invDisplay}</span>
                                        </Box>
                                      ) : (
                                        <Tooltip title={canPreview ? "Preview invoice (opens in new tab)" : "Preview available only for finalized or sent invoices"}>
                                          <Box
                                            component="span"
                                            onClick={canPreview ? () => handlePreviewInvoice(customer.org_code, inv.invoice_id) : undefined}
                                            sx={{
                                              display: "block",
                                              color: canPreview ? "#3b82f6" : "#94a3b8",
                                              cursor: canPreview ? "pointer" : "default",
                                              textDecoration: canPreview ? "underline" : "none",
                                              "&:hover": canPreview ? { color: "#2563eb" } : {},
                                            }}
                                          >
                                            {invDisplay}
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  ) : null;
                                })()}
                              </Box>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell sx={{ color: "#71717a", fontFamily: "inherit" }}>
                            {customer.next_bill_date ? formatDate(customer.next_bill_date) : "—"}
                          </TableCell>
                          <TableCell sx={{ color: "#71717a", fontFamily: "inherit" }}>
                            <Tooltip title={`Billing rules for ${customer.org_name || "customer"}`}>
                              <Chip
                                icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                                label={billingRulesCount}
                                size="small"
                                onClick={() => history.push(`/billing-management/customer/${customer.org_code}/rules`)}
                                sx={{
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  fontSize: "0.8125rem",
                                  fontFamily: "inherit",
                                  borderRadius: "6px",
                                  backgroundColor: "#f1f5f9",
                                  color: "#475569",
                                  "&:hover": { backgroundColor: "#e2e8f0", color: "#334155" },
                                  "& .MuiChip-icon": { color: "inherit" },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ fontFamily: "inherit", px: 1.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5 }}>
                              <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
                                {customer.has_on_demand_invoice_generation_in_progress && (
                                  <Tooltip title="Invoice generation is in progress">
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "primary.main",
                                        "@keyframes invoiceProgressBlink": {
                                          "0%, 100%": { opacity: 1 },
                                          "50%": { opacity: 0.35 },
                                        },
                                        animation: "invoiceProgressBlink 1.2s ease-in-out infinite",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Button
                                type="button"
                                size="small"
                                variant="contained"
                                onClick={() => handleOpenGenerateDialog(customer)}
                                sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.8125rem", fontFamily: "inherit", borderRadius: "6px", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
                              >
                                Generate
                              </Button>
                              <Tooltip title={`View invoices for ${customer.org_name || "customer"}`}>
                                <IconButton
                                  size="small"
                                  onClick={() => history.push(`/billing-management/customer/${customer.org_code}/invoices`, { customerName: customer.org_name })}
                                  sx={{ color: "#71717a", "&:hover": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  !customer.latest_invoice?.invoice_id
                                    ? "No invoice available"
                                    : !canDownloadInvoice(customer.latest_invoice)
                                      ? "Download available only for Finalized or Sent invoices"
                                      : "Download invoice"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      !canDownloadInvoice(customer.latest_invoice) ||
                                      downloadingInvoiceId === customer.latest_invoice?.invoice_id
                                    }
                                    onClick={() => handleDownloadInvoice(customer.org_code, customer.latest_invoice?.invoice_id)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    {downloadingInvoiceId === customer.latest_invoice?.invoice_id ? (
                                      <CircularProgress size={20} sx={{ color: "#71717a" }} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </CardContent>
      </Card>

      <Dialog
        open={generateDialogOpen}
        onClose={handleCloseGenerateDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {generateNoRules ? "No rules for selected period" : "Generate invoice"}
        </DialogTitle>
        <DialogContent>
          {generateNoRules ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mb: 1 }}>
                {generateNoRulesMessage}
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.8125rem", fontWeight: 500 }}>
                At least one rule needs to be applied for the selected date range before you can generate an invoice.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }} component="div">
              {generateCustomer && (
                <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                  Customer: <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>{generateCustomer.org_name || generateCustomer.org_code}</Box>
                </Typography>
              )}
              <FormControl size="small" fullWidth required>
                <InputLabel required>Year</InputLabel>
                <Select
                  value={generateYear}
                  label="Year"
                  onChange={(e) => setGenerateYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth required>
                <InputLabel required>Month</InputLabel>
                <Select
                  value={generateMonth}
                  label="Month"
                  onChange={(e) => setGenerateMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <MenuItem key={name} value={i + 1}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth required>
                <InputLabel id="generate-po-label" shrink required>Purchase Order</InputLabel>
                <Select
                  labelId="generate-po-label"
                  value={generatePoId}
                  label="Purchase Order"
                  onChange={(e) => setGeneratePoId(e.target.value)}
                  displayEmpty
                  inputProps={{ "aria-label": "Purchase Order" }}
                  renderValue={(v) => {
                    if (!v) return "None";
                    const po = generatePoList.find((p) => p.id === v);
                    if (!po) return "None";
                    return po.order_number ? `${po.po_number ?? ""} (${po.order_number})` : (po.po_number ?? "—");
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {generatePoList.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.order_number ? `${po.po_number ?? "—"} (${po.order_number})` : (po.po_number ?? "—")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {generateError && (
                <Alert severity="error" sx={{ fontSize: "0.8125rem" }} onClose={() => setGenerateError(null)}>
                  {generateError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          {generateNoRules ? (
            <Button type="button" variant="contained" onClick={handleCloseGenerateDialog} sx={{ textTransform: "none", fontWeight: 600 }}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" onClick={handleCloseGenerateDialog} disabled={generateLoading} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={generateLoading || !(generatePoId != null && String(generatePoId).trim() !== "")}
                onClick={() => handleGenerateInvoice(false)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {generateLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Generate"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={downloadSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))}
          severity={downloadSnackbar.severity}
        >
          {downloadSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingManagement;
